import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import WidgetCard from './WidgetCard';
import MainCardTitle from './MainCardTitle';
import CenteredSpinner from '../../../components/CenteredSpinner';
import DashboardServices from '../../../services/DashboardServices';
import localStorageService from '../../../services/localStorageService';

const COLORS = [
    '#5e35b1', // purple
    '#7cb342', // green
    '#d4a017', // gold
    '#ef5350', // red
    '#ec407a', // pink
];

const CustomLegend = ({ data }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {data.map((item, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="center">
                <Box
                    sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: item.color,
                        flexShrink: 0
                    }}
                />
                <Typography variant="body2" sx={{ flex: 1, color: 'text.primary' }}>
                    {item.name}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {item.value}
                </Typography>
            </Stack>
        ))}
    </Box>
);

const TopCategories = ({ title = "Categories wise workload" }) => {
    const [loading, setLoading] = useState(true);
    const [chartData, setChartData] = useState([]);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            try {
                // Get params with organization_id if applicable
                const storedUser = localStorageService?.getItem?.('user') ?? null;
                const roles = (storedUser?.roles) || (storedUser?.role) || storedUser?.rolesArray || [];
                const normalizedRoles = Array.isArray(roles) ? roles.map(r => String(r).toLowerCase()) : [];

                // Check if user is Super Admin
                const isSuperAdmin = normalizedRoles.includes('super admin');

                const targetRoles = ['organization admin', 'case officer', 'case manager', 'organization head', 'director cid', 'director ncpa', 'deputy director','oic','department head','team leader first respondent','call center officer mowca', 'counselling officer', 'legal officer'];
                const hasTargetRole = normalizedRoles.some(r => targetRoles.includes(r));

                const params = {};

                if (hasTargetRole) {
                    const orgId = storedUser?.employee?.organization_id ?? storedUser?.organization_id ?? storedUser?.org?.id ?? null;
                    if (orgId) params.organization_id = orgId;
                }
                const targetEmployee_id_Roles = ['case officer', 'first respondent', 'counselling officer', 'legal officer'];
                const needToPassUserId = normalizedRoles.some(r => targetEmployee_id_Roles.includes(r));
                if (needToPassUserId) {
                    const employeeId = storedUser?.employee?.id ?? storedUser?.employee_id ?? null;
                    if (employeeId) params.assigned_to_officer_id = employeeId;
                }

                // Use different endpoint for Super Admin
                const res = isSuperAdmin
                    ? await DashboardServices.getDashboardTopCategoriesCountryLevel(params)
                    : await DashboardServices.getDashboardTopCategories(params);
                const payload = res?.data ?? res;
                const dataArray = payload?.view?.data ?? payload?.data ?? payload ?? [];

                // Transform data for chart
                const formatted = Array.isArray(dataArray)
                    ? dataArray.map((item, index) => ({
                        name: item.category_name ?? item.name ?? `Category ${index + 1}`,
                        value: Number(item.case_count) || 0,
                        category_id: item.category_id,
                        color: COLORS[index % COLORS.length]
                    }))
                    : [];

                console.log('Top Categories Data:', formatted);
                if (mounted) setChartData(formatted);
            } catch (err) {
                console.error('Failed to load top categories data', err);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        load();
        return () => { mounted = false; };
    }, []);

    //if (loading) return <WidgetCard><CenteredSpinner /></WidgetCard>;

    return (
        <WidgetCard>
            <MainCardTitle>{title}</MainCardTitle>

            {chartData.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                    No data available
                </Typography>
            ) : (
                <Box sx={{ display: 'flex', mt: 2 }}>
                    {/* Chart */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                                data={chartData}
                                margin={{ top: 20, right: 10, left: -10, bottom: 20 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                <XAxis
                                    dataKey="name"
                                    tick={false}
                                    axisLine={{ stroke: '#e0e0e0' }}
                                />
                                <YAxis
                                    tick={{ fill: '#666', fontSize: 12 }}
                                    axisLine={{ stroke: '#e0e0e0' }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: '#fff',
                                        border: '1px solid #ccc',
                                        borderRadius: 4
                                    }}
                                    cursor={{ fill: 'rgba(0,0,0,0.05)' }}
                                />
                                <Bar
                                    dataKey="value"
                                    radius={[8, 8, 0, 0]}
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>

                    {/* Legend */}
                    <Box sx={{ width: 250, flexShrink: 0, maxHeight: 300, overflowY: 'auto', pr: 1 }}>
                        <CustomLegend data={chartData} />
                    </Box>
                </Box>
            )}
        </WidgetCard>
    );
};

export default TopCategories;
